import { useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Video, Clapperboard, X, Play, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { uploadVideoChunked } from "@/lib/uploadVideo";
import GifPicker from "@/components/GifPicker";

const MAX_IMAGES = 4;
const MAX_VIDEOS = 3;

const videoDuration = (file) => new Promise((resolve) => {
    try {
        const url = URL.createObjectURL(file);
        const v = document.createElement("video");
        v.preload = "metadata";
        v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration || 0); };
        v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
        v.src = url;
    } catch { resolve(0); }
});

/**
 * Inline media editor used when editing a post/reply. Manages images, videos and a
 * GIF. `value` = { images, videos, gif }; calls onChange with the next media object.
 */
export default function MediaEditor({ value, onChange, accent = "#ff4500", showPhoto = true, showVideo = true, showGif = true, testidPrefix = "media-editor" }) {
    const images = value.images || [];
    const videos = value.videos || [];
    const gif = value.gif || null;
    const [uploading, setUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoPct, setVideoPct] = useState(0);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const fileRef = useRef(null);
    const videoRef = useRef(null);

    const pickImages = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_IMAGES - images.length;
        if (room <= 0) return toast.error(`Up to ${MAX_IMAGES} photos`);
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.url);
            }
            onChange({ ...value, images: [...images, ...uploaded], gif: null });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const pickVideos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_VIDEOS - videos.length;
        if (room <= 0) return toast.error(`Up to ${MAX_VIDEOS} videos`);
        setVideoUploading(true);
        setVideoPct(0);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const dur = await videoDuration(f);
                if (dur && dur > 31) { toast.error(`"${f.name}" is longer than 30 seconds — trim it first`); continue; }
                const data = await uploadVideoChunked(f, setVideoPct);
                uploaded.push({ url: data.url, poster: data.poster });
            }
            if (uploaded.length) onChange({ ...value, videos: [...videos, ...uploaded], gif: null });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Video upload failed");
        } finally { setVideoUploading(false); setVideoPct(0); }
    };

    const removeImage = (i) => onChange({ ...value, images: images.filter((_, idx) => idx !== i) });
    const removeVideo = (i) => onChange({ ...value, videos: videos.filter((_, idx) => idx !== i) });

    return (
        <div className="mt-2" onClick={(e) => e.stopPropagation()} data-testid={testidPrefix}>
            {images.length > 0 && (
                <div className="grid grid-cols-3 gap-1.5" data-testid={`${testidPrefix}-images`}>
                    {images.map((url, i) => (
                        <div key={i} className="relative">
                            <img src={url} alt="" className="h-20 w-full object-cover rounded-lg bg-[#0a0a0a]" />
                            <button type="button" data-testid={`${testidPrefix}-remove-image-${i}`} onClick={() => removeImage(i)} className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"><X className="h-3 w-3" /></button>
                        </div>
                    ))}
                </div>
            )}
            {(videos.length > 0 || videoUploading) && (
                <div className="grid grid-cols-3 gap-1.5 mt-1.5" data-testid={`${testidPrefix}-videos`}>
                    {videos.map((v, i) => (
                        <div key={i} className="relative aspect-video">
                            {v.poster ? <img src={v.poster} alt="" className="h-full w-full object-cover rounded-lg bg-black" /> : <div className="h-full w-full rounded-lg bg-black" />}
                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/60"><Play className="h-3.5 w-3.5 text-white" fill="white" /></span></span>
                            <button type="button" data-testid={`${testidPrefix}-remove-video-${i}`} onClick={() => removeVideo(i)} className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"><X className="h-3 w-3" /></button>
                        </div>
                    ))}
                    {videoUploading && <div className="aspect-video rounded-lg bg-[#0f0f0f] border border-[#2a2a2a] flex flex-col items-center justify-center gap-1 text-[#a0a0a0]"><Loader2 className="h-5 w-5 animate-spin" /><span className="text-[10px] uppercase tracking-wider">{videoPct > 0 && videoPct < 100 ? `Uploading ${videoPct}%` : "Processing…"}</span></div>}
                </div>
            )}
            {gif && (
                <div className="relative w-fit mt-1.5">
                    <img src={gif.full_url} alt="GIF" className="max-h-40 rounded-lg" data-testid={`${testidPrefix}-gif`} />
                    <button type="button" data-testid={`${testidPrefix}-remove-gif`} onClick={() => onChange({ ...value, gif: null })} className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black"><X className="h-3 w-3" /></button>
                </div>
            )}
            <div className="mt-2 flex items-center gap-1 relative">
                {showPhoto && (
                    <>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickImages} data-testid={`${testidPrefix}-file`} />
                        <button type="button" title="Add photo" data-testid={`${testidPrefix}-add-photo`} onClick={() => fileRef.current?.click()} disabled={!!gif || uploading} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-30" style={{ color: accent }}>{uploading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <ImageIcon className="h-[18px] w-[18px]" />}</button>
                    </>
                )}
                {showVideo && (
                    <>
                        <input ref={videoRef} type="file" accept="video/*" multiple hidden onChange={pickVideos} data-testid={`${testidPrefix}-video-file`} />
                        <button type="button" title="Add video (30s max)" data-testid={`${testidPrefix}-add-video`} onClick={() => videoRef.current?.click()} disabled={!!gif || videoUploading || videos.length >= MAX_VIDEOS} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-30" style={{ color: accent }}>{videoUploading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Video className="h-[18px] w-[18px]" />}</button>
                    </>
                )}
                {showGif && (
                    <button type="button" title="Add GIF" data-testid={`${testidPrefix}-add-gif`} onClick={() => setShowGifPicker((s) => !s)} disabled={images.length > 0 || videos.length > 0} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/5 disabled:opacity-30" style={{ color: accent }}><Clapperboard className="h-[18px] w-[18px]" /></button>
                )}
                {showGifPicker && <GifPicker accent={accent} onClose={() => setShowGifPicker(false)} onChoose={(g) => { onChange({ ...value, gif: g, images: [], videos: [] }); setShowGifPicker(false); }} />}
            </div>
        </div>
    );
}

export { MAX_IMAGES, MAX_VIDEOS };
